import clsx from 'clsx'
import { useField } from 'formik'
import { FC, SVGProps, useEffect } from 'react'
import MaskedInput, { MaskedInputProps as ReactTextMaskedInput } from 'react-text-mask'

import { InputContainer, InputStyled } from './Input.styles'

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  as?: React.ReactNode
  className?: string
  error?: string
  hint?: string
  id: string
  isMasked?: false
  isOptional?: boolean
  label?: string
  name: string
  placeholder?: string
  renderHint?: FC
  renderLabel?: FC
  renderLeftIcon?: FC<SVGProps<SVGSVGElement>>
  renderRightIcon?: FC<SVGProps<SVGSVGElement>>
  showSuccess?: boolean
}

type MaskedInputProps = Omit<InputProps, 'isMasked'> & {
  guide?: ReactTextMaskedInput['guide']
  inputMode: ReactTextMaskedInput['inputMode']
  isMasked: true
  mask?: ReactTextMaskedInput['mask']
}

export function Input({
  as,
  className = '',
  disabled,
  hint: propsHint,
  error: propsError,
  id,
  isMasked,
  label,
  name,
  isOptional,
  placeholder = '',
  showSuccess = false,
  type = 'text',
  renderLabel: Label,
  renderHint: Hint,
  renderLeftIcon: LeftIcon,
  renderRightIcon: RightIcon,
  ...restProps
}: InputProps | MaskedInputProps) {
  const [{ value = '', onChange, ...field }, { touched, error }, { setError }] = useField({ name, type, placeholder })

  const isSuccess = showSuccess && touched && !error
  const isError = !!error
  const hint = (isError && error) || propsHint

  const inputProps = {
    id,
    disabled,
    value: value === 0 || value === null ? '' : value,
    type,
    style: { fontFamily: type === 'password' ? 'sans-serif' : '' },
    css: InputStyled,
    className: clsx(LeftIcon && 'pl-12', RightIcon && 'pr-12'),
    placeholder: placeholder,
    spellCheck: false,
    onChange: handleChange,
    ...(restProps as any),
    ...field,
  }

  function handleChange(e: React.ChangeEvent) {
    onChange(e)
    setError(undefined)
  }

  useEffect(() => {
    setError(propsError)
    // * setError is not a useCallback from useField
    //eslint-disable-next-line
  }, [propsError])

  return (
    <InputContainer
      className={`flex flex-col ${className}`}
      isDisabled={disabled}
      isError={isError}
      isSuccess={isSuccess}
    >
      {(label || Label) && (
        <label className="text-primary mb-2 font-medium leading-120" htmlFor={id}>
          {Label ? <Label /> : label}
        </label>
      )}
      <div className="relative flex items-center">
        {LeftIcon && <LeftIcon />}
        {isMasked ? <MaskedInput {...inputProps} /> : <input {...inputProps} />}
        {RightIcon && <RightIcon />}
      </div>
      {(hint || Hint) && <p className="text-tertiary mt-1 font-medium text-label-xs">{Hint ? <Hint /> : hint}</p>}{' '}
    </InputContainer>
  )
}
